import React from "react";
import {
  Button,
  Grid,
  TextField,
  Typography,
  Checkbox,
  Box,
  CardContent,
  CircularProgress,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
} from "@mui/material";
import { formatDateString } from "../../../helpers";
import { useState } from "react";
import ListadoDeProductos from "../../../components/ListadoDeProductos";
import ingressService from "../../../services/ingressService";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { useEffect } from "react";
import LogoPdf from "../../../../src/assets/LogoPdf.png";
import ReceiptIcon from "@mui/icons-material/Receipt";
import BusinessIcon from "@mui/icons-material/Business";
import NotesIcon from "@mui/icons-material/Notes";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import { warehouseRemitenteLines } from "../../../utils/remitente";

const ModalListadoIngresos = ({
  loading: externalLoading,
  ingressDetail,
  depositName,
  handleCloseDetailModal,
  isEdit,
  fetchAllIngress,
  setAlertObj,
  setOpenSnack,
  onConfirmSuccess,
}) => {
  const [amountEntered, setAmountEntered] = useState({});
  const [erroresCantidad, setErroresCantidad] = useState({});
  const [inputFocus, setInputFocus] = useState(null);
  const [internalLoading, setInternalLoading] = useState(false);
  const [selectedProducts, setSelectedProducts] = useState([]);

  const isLoading = externalLoading || internalLoading;

  useEffect(() => {
    if (ingressDetail && ingressDetail.movement_details) {
      const productsWithPrintTag = ingressDetail.movement_details.filter(
        (item) => item.print_tag === true
      );

      setSelectedProducts((prevSelected) => {
        const newSelectedProducts = [...prevSelected];

        productsWithPrintTag.forEach((product) => {
          const isAlreadySelected = newSelectedProducts.some(
            (selectedProduct) => selectedProduct.ean_id === product.ean_id
          );
          if (!isAlreadySelected) {
            newSelectedProducts.push({
              ean_id: product.ean_id,
              name: product.description,
            });
          }
        });

        return newSelectedProducts;
      });
    }
  }, [ingressDetail]);

  // Grupo de datos: un solo ícono + título; los campos van debajo, sin ícono propio.
  const DetailGroup = ({ icon, title, children }) => (
    <Box sx={{ mb: 1.5 }}>
      <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.6 }}>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            width: 26,
            height: 26,
            borderRadius: "7px",
            bgcolor: "#E8F0FE",
            flexShrink: 0,
          }}
        >
          {React.cloneElement(icon, { sx: { fontSize: 15, color: "primary.main" } })}
        </Box>
        <Typography
          sx={{
            fontWeight: 700,
            color: "#475569",
            letterSpacing: "0.06em",
            fontSize: "0.7rem",
            textTransform: "uppercase",
          }}
        >
          {title}
        </Typography>
      </Box>
      <Box sx={{ pl: "34px" }}>{children}</Box>
    </Box>
  );

  const DetailField = ({ label, value }) => (
    <Box sx={{ display: "flex", gap: 1.5, mb: 0.4, alignItems: "baseline" }}>
      <Typography sx={{ color: "#94a3b8", fontSize: "0.72rem", minWidth: 110, flexShrink: 0, lineHeight: 1.4 }}>
        {label}
      </Typography>
      <Typography sx={{ fontWeight: 600, fontSize: "0.8rem", color: "#1e293b", lineHeight: 1.4, wordBreak: "break-word", minWidth: 0 }}>
        {value ?? "—"}
      </Typography>
    </Box>
  );

  const handleConfirm = async () => {
    setInternalLoading(true);

    ingressDetail.movement_details.forEach((detalle) => {
      if (amountEntered[detalle.product_id] === undefined) {
        setAmountEntered((prevAmountEntered) => ({
          ...prevAmountEntered,
          [detalle.product_id]: detalle.effective_count,
        }));
      }
    });

    const allAmountsEntered = ingressDetail.movement_details?.every(
      (detalle) => amountEntered[detalle.product_id] !== undefined
    );

    if (!allAmountsEntered) {
      setInternalLoading(false);
      setAlertObj({
        severity: "error",
        message: "Por favor, ingrese todas las cantidades",
      });
      setOpenSnack(true);
      return;
    }

    const ingressData = {
      id: ingressDetail.id,
      type: ingressDetail.type,
      status: "accepted",
      sub_movement_id: ingressDetail.sub_movement_id,
      receipt_id: ingressDetail.receipt_id,
      provider_name: ingressDetail.provider_name,
      warehouse_id: ingressDetail.warehouse_id,
      email: ingressDetail.email,
      observation: ingressDetail.observation,
      phone_number: ingressDetail.phone_number,
      user_id: ingressDetail.user_id,
      movement_details: ingressDetail.movement_details.map((detalle) => ({
        product_id: detalle.product_id,
        description: detalle.description,
        original_count: detalle.original_count,
        effective_count: parseInt(amountEntered[detalle.product_id] || 0),
      })),
    };

    try {
      const result = await ingressService.postRegisterIngress(ingressData);
      if (result?.data) {
        setAlertObj({
          severity: "success",
          message: "Confirmación exitosa",
        });
        handleDownloadPDF();
        setOpenSnack(true);
        const expandedProducts = ingressDetail.movement_details.flatMap(
          (detalle) => {
            const isSelected = selectedProducts.some(
              (prod) => prod.ean_id === detalle.ean_id
            );

            const cantidad = parseInt(
              amountEntered[detalle.product_id] || 0,
              10
            );

            if (!isSelected || !cantidad || cantidad <= 0) return [];

            return Array.from({ length: cantidad }, () => ({
              ean_id: detalle.ean_id,
              sku_id: detalle.sku_id,
              name: detalle.description,
              amountEntered: cantidad,
            }));
          }
        );

        console.log(expandedProducts);

        onConfirmSuccess(expandedProducts);
      } else {
        setAlertObj({
          severity: "error",
          message: result.data,
        });
        setOpenSnack(true);
      }
    } catch (error) {
      console.error("Error al confirmar", error);
    } finally {
      setInternalLoading(false);
      fetchAllIngress();
      handleCloseDetailModal();
    }
  };

  const handleCheckboxChange = (product) => {
    setSelectedProducts((prevSelected) => {
      const isSelected = prevSelected.some(
        (selectedProduct) => selectedProduct.ean_id === product.ean_id
      );

      if (isSelected) {
        return prevSelected.filter(
          (selectedProduct) => selectedProduct.ean_id !== product.ean_id
        );
      } else {
        return [
          ...prevSelected,
          {
            ean_id: product.ean_id,
            sku_id: product.sku_id,
            name: product.description,
          },
        ];
      }
    });
  };

  const handleQuantityChange = (event, sku) => {
    const newValue = event.target.value;

    if (!/^\d*$/.test(newValue)) {
      setErroresCantidad((prevErroresCantidad) => ({
        ...prevErroresCantidad,
        [sku]: "Solo se permiten números",
      }));
      return;
    }

    setAmountEntered((prevAmountEntered) => ({
      ...prevAmountEntered,
      [sku]: newValue,
    }));

    setErroresCantidad((prevErroresCantidad) => ({
      ...prevErroresCantidad,
      [sku]: newValue.trim() === "" ? "Ingrese la cantidad" : null,
    }));

    setInputFocus(sku);
  };

  const handleDownloadPDF = async () => {
    const doc = new jsPDF();

    const optimizedLogo = await optimizeImage(LogoPdf, {
      maxWidth: 300,
      maxHeight: 240,
      quality: 0.8,
    });

    doc.setFont("Helvetica");

    const logoWidth = 25;
    const logoHeight = 20;
    doc.addImage(
      optimizedLogo,
      "PNG",
      170,
      10,
      logoWidth,
      logoHeight,
      undefined,
      "FAST"
    );

    doc.setFontSize(20);
    doc.setFont("Helvetica", "bold");
    doc.text("DETALLE DE INGRESO", 105, 20, { align: "center" });

    let verticalSpace = 55;

    doc.setFontSize(12);
    doc.setFont("Helvetica", "normal");
    // Encabezado = depósito (configurable en /profile).
    const remitenteLines = warehouseRemitenteLines(ingressDetail);
    let headerY = 30;
    (remitenteLines.length ? remitenteLines : [""]).slice(0, 3).forEach((ln) => {
      doc.text(ln, 105, headerY, { align: "center" });
      headerY += 5;
    });

    doc.line(15, 45, 195, 45);

    doc.setFontSize(10);

    doc.setFont("Helvetica", "bold");
    doc.text("Información del ingreso", 20, verticalSpace);

    doc.setFont("Helvetica", "normal");
    verticalSpace += 8;

    doc.text(
      `Fecha de Creación: ${formatDateString(ingressDetail.date_created)}`,
      20,
      verticalSpace
    );
    doc.text(`Id Orden: ${ingressDetail.id}`, 110, verticalSpace);

    verticalSpace += 5;
    doc.text(`Proveedor: ${ingressDetail.provider_name}`, 20, verticalSpace);

    verticalSpace += 5;
    doc.text(`Comprobante: ${ingressDetail.receipt_id}`, 20, verticalSpace);
    doc.text(`Depósito: ${depositName}`, 110, verticalSpace);

    verticalSpace += 5;

    doc.text("Observaciones:", 20, verticalSpace);

    verticalSpace += 5;

    if (ingressDetail.observation) {
      const maxWidth = 75;
      const observationLines = doc.splitTextToSize(
        ingressDetail.observation,
        maxWidth
      );
      doc.text(observationLines, 20, verticalSpace);

      verticalSpace += observationLines.length * 4;
    } else {
      doc.text("Sin observaciones", 20, verticalSpace);
      verticalSpace += 4;
    }

    verticalSpace += 6;
    doc.line(15, verticalSpace, 195, verticalSpace);

    verticalSpace += 10;
    const data = ingressDetail.movement_details.map((producto) => [
      producto.sku_id,
      producto.description,
      producto.original_count.toString(),
      (amountEntered[producto.product_id] || producto.effective_count || 0) ===
      0
        ? ""
        : (
            amountEntered[producto.product_id] ||
            producto.effective_count ||
            0
          ).toString(),
    ]);
    autoTable(doc, {
      startY: verticalSpace,
      head: [
        ["SKU", "Descripción", "Cantidad declarada", "Cantidad ingresada"],
      ],
      body: data,
      columnStyles: {
        0: { cellWidth: 40 },
        1: { cellWidth: 90 },
        2: { cellWidth: 25 },
        3: { cellWidth: 25 },
      },
      styles: {
        overflow: "linebreak",
        halign: "center",
        valign: "middle",
      },
    });

    verticalSpace = doc.lastAutoTable.finalY + 10;

    doc.setFont("Helvetica", "bold");
    const totalSKU = ingressDetail.movement_details.length;

    const completedSKU = ingressDetail.movement_details.filter(
      (detail) =>
        (amountEntered[detail.product_id] || detail.effective_count || 0) > 0
    ).length;

    const totalUnidadesIngresadas1 = ingressDetail.movement_details.reduce(
      (total, detail) =>
        total +
        Number(amountEntered[detail.product_id] || detail.effective_count || 0),
      0
    );

    const totalUnidadesOriginales = ingressDetail.movement_details.reduce(
      (total, detail) => total + (detail.original_count || 0),
      0
    );

    doc.text(
      `Total de productos: ${totalSKU} / ${completedSKU}`,
      20,
      verticalSpace
    );
    verticalSpace += 5;
    doc.text(
      `Total de unidades: ${totalUnidadesOriginales} / ${totalUnidadesIngresadas1}`,
      20,
      verticalSpace
    );

    doc.save(`Detalle_ingreso_${ingressDetail.id}.pdf`);
  };

  const optimizeImage = (imageSource, options = {}) => {
    return new Promise((resolve) => {
      const img = new Image();
      img.onload = () => {
        const canvas = document.createElement("canvas");

        let width = img.width;
        let height = img.height;

        if (options.maxWidth && width > options.maxWidth) {
          height = (options.maxWidth * height) / width;
          width = options.maxWidth;
        }

        if (options.maxHeight && height > options.maxHeight) {
          width = (options.maxHeight * width) / height;
          height = options.maxHeight;
        }

        canvas.width = width;
        canvas.height = height;

        const ctx = canvas.getContext("2d");
        ctx.drawImage(img, 0, 0, width, height);

        resolve(canvas.toDataURL("image/png", options.quality || 0.8));
      };
      img.src = imageSource;
    });
  };
  if (isLoading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: "300px",
          flexDirection: "column",
          gap: 2,
        }}
      >
        <CircularProgress size={60} />
      </Box>
    );
  }

  return (
    <>
      {!isEdit ? (
        <>
          <Box
            sx={{
              backgroundColor: "#f8fafc",
              borderRadius: "12px",
              border: "1px solid #e2e8f0",
              p: 1.75,
              mb: 1.5,
            }}
          >
            <Grid container spacing={0} columnSpacing={4}>
              <Grid item xs={12} md={6}>
                <DetailGroup icon={<ReceiptIcon fontSize="small" />} title="Orden">
                  <DetailField label="Fecha creación" value={formatDateString(ingressDetail?.date_created)} />
                  <DetailField label="Id orden" value={ingressDetail?.id} />
                  <DetailField label="Comprobante" value={ingressDetail?.receipt_id || "—"} />
                </DetailGroup>
              </Grid>

              <Grid item xs={12} md={6}>
                <DetailGroup icon={<BusinessIcon fontSize="small" />} title="Origen">
                  <DetailField label="Proveedor" value={ingressDetail?.provider_name || "—"} />
                  <DetailField label="Depósito" value={depositName} />
                </DetailGroup>
              </Grid>
            </Grid>

            <Divider sx={{ my: 1 }} />

            <DetailGroup icon={<NotesIcon fontSize="small" />} title="Observaciones">
              <Typography sx={{ fontSize: "0.8rem", color: "#1e293b", lineHeight: 1.4 }}>
                {ingressDetail?.observation || "Sin observaciones"}
              </Typography>
            </DetailGroup>
          </Box>

          <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1.5, color: "#334155" }}>
            Productos
          </Typography>
          <ListadoDeProductos
            productos={ingressDetail.movement_details}
            withDeclared={true}
          />
        </>
      ) : (
        <>
          <TableContainer
            component={Paper}
            elevation={0}
            sx={{
              maxHeight: 380,
              borderRadius: "10px",
              border: "1px solid #e2e8f0",
              mb: 2.5,
            }}
          >
            <Table stickyHeader size="small">
              <TableHead>
                <TableRow>
                  <TableCell
                    sx={{
                      fontWeight: 600,
                      fontSize: "0.8rem",
                      color: "#475569",
                      backgroundColor: "#f1f5f9",
                      borderBottom: "2px solid #e2e8f0",
                      py: 1.5,
                    }}
                  >
                    SKU
                  </TableCell>
                  <TableCell
                    sx={{
                      fontWeight: 600,
                      fontSize: "0.8rem",
                      color: "#475569",
                      backgroundColor: "#f1f5f9",
                      borderBottom: "2px solid #e2e8f0",
                      py: 1.5,
                    }}
                  >
                    Descripción
                  </TableCell>
                  <TableCell
                    align="center"
                    sx={{
                      fontWeight: 600,
                      fontSize: "0.8rem",
                      color: "#475569",
                      backgroundColor: "#f1f5f9",
                      borderBottom: "2px solid #e2e8f0",
                      py: 1.5,
                      whiteSpace: "nowrap",
                    }}
                  >
                    Etiqueta
                  </TableCell>
                  <TableCell
                    align="center"
                    sx={{
                      fontWeight: 600,
                      fontSize: "0.8rem",
                      color: "#475569",
                      backgroundColor: "#f1f5f9",
                      borderBottom: "2px solid #e2e8f0",
                      py: 1.5,
                      whiteSpace: "nowrap",
                    }}
                  >
                    Declarada
                  </TableCell>
                  <TableCell
                    align="center"
                    sx={{
                      fontWeight: 600,
                      fontSize: "0.8rem",
                      color: "#475569",
                      backgroundColor: "#f1f5f9",
                      borderBottom: "2px solid #e2e8f0",
                      py: 1.5,
                      whiteSpace: "nowrap",
                    }}
                  >
                    Ingresada
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {ingressDetail.movement_details.map((item, index) => (
                  <TableRow
                    key={item.product_id}
                    sx={{
                      backgroundColor: index % 2 === 0 ? "#fff" : "#fafbfc",
                      "&:hover": { backgroundColor: "#f0f4ff" },
                      transition: "background-color 0.15s",
                    }}
                  >
                    <TableCell sx={{ py: 1.5 }}>
                      <Chip
                        label={item.sku_id}
                        size="small"
                        sx={{
                          fontWeight: 600,
                          fontSize: "0.75rem",
                          backgroundColor: "#E8F0FE",
                          color: "#3949ab",
                        }}
                      />
                    </TableCell>
                    <TableCell sx={{ py: 1.5 }}>
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        {item.description}
                      </Typography>
                    </TableCell>
                    <TableCell align="center" sx={{ py: 1.5 }}>
                      <Checkbox
                        size="small"
                        sx={{
                          p: 0,
                          color: "#94a3b8",
                          "&.Mui-checked": { color: "primary.main" },
                        }}
                        checked={
                          selectedProducts.some(
                            (selectedProduct) =>
                              selectedProduct.ean_id === item.ean_id
                          ) || item.print_tag === true
                        }
                        onChange={() => handleCheckboxChange(item)}
                      />
                    </TableCell>
                    <TableCell align="center" sx={{ py: 1.5 }}>
                      <Typography variant="body2" fontWeight={500}>
                        {item.original_count}
                      </Typography>
                    </TableCell>
                    <TableCell align="center" sx={{ py: 1.5 }}>
                      <TextField
                        variant="outlined"
                        size="small"
                        type="text"
                        placeholder="0"
                        sx={{
                          width: 72,
                          "& .MuiOutlinedInput-root": {
                            borderRadius: "8px",
                            fontSize: "0.875rem",
                            "& fieldset": { borderColor: "#cbd5e1" },
                            "&:hover fieldset": { borderColor: "primary.main" },
                            "&.Mui-focused fieldset": { borderWidth: 2 },
                          },
                          "& input": { textAlign: "center", py: 0.8 },
                        }}
                        value={amountEntered[item.product_id] || ""}
                        onChange={(event) =>
                          handleQuantityChange(event, item.product_id)
                        }
                        inputProps={{
                          pattern: "[0-9]*",
                          title: "Ingrese solo números",
                        }}
                        autoFocus={inputFocus === item.product_id}
                        error={erroresCantidad[item.product_id] !== null}
                        helperText={erroresCantidad[item.product_id]}
                      />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          <Divider sx={{ mb: 2 }} />

          <Box
            sx={{
              display: "flex",
              justifyContent: "flex-end",
              gap: 1.5,
              pb: 0.5,
            }}
          >
            <Button
              variant="outlined"
              onClick={handleCloseDetailModal}
              sx={{
                borderRadius: "8px",
                textTransform: "none",
                fontWeight: 600,
                px: 3,
                borderColor: "#cbd5e1",
                color: "#475569",
                "&:hover": {
                  borderColor: "#94a3b8",
                  backgroundColor: "#f8fafc",
                },
              }}
            >
              Cancelar
            </Button>
            <Button
              variant="contained"
              color="primary"
              onClick={handleConfirm}
              disabled={internalLoading}
              startIcon={!internalLoading && <CheckCircleOutlineIcon />}
              sx={{
                borderRadius: "8px",
                textTransform: "none",
                fontWeight: 600,
                px: 3,
              }}
            >
              {internalLoading ? "Confirmando..." : "Confirmar ingreso"}
            </Button>
          </Box>
        </>
      )}
    </>
  );
};
export default ModalListadoIngresos;
